// Portions of this code are adapted from the MUI Toolpad PageHeader component.
// Source: https://github.com/mui/toolpad/tree/master/packages/toolpad-core/src/PageContainer
// MUI is licensed under the MIT License: https://github.com/mui/toolpad/blob/master/LICENSE
import * as React from 'react';

import { styled } from '@mui/material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useSlotProps from '@mui/utils/useSlotProps';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, UIMatch, useMatches } from 'react-router';

import type { Breadcrumb } from './PageContainer';
import { PageHeaderToolbar, PageHeaderToolbarProps } from './PageHeaderToolbar';

const PageContentHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

export interface PageHeaderSlotProps {
  toolbar: PageHeaderToolbarProps;
}

export interface PageHeaderSlots {
  /**
   * The component that renders the actions toolbar.
   * @default PageHeaderToolbar
   */
  toolbar: React.ElementType;
}

export interface PageHeaderProps {
  /**
   * The title of the page. Leave blank to use the active page title.
   */
  title?: string;
  /**
   * Optional subtitle of the page.
   */
  subtitle?: string;
  /**
   * The breadcrumbs of the page. Leave blank to use the active page breadcrumbs.
   */
  breadcrumbs?: Breadcrumb[];
  /**
   * The components used for each slot inside.
   */
  slots?: PageHeaderSlots;
  /**
   * The props used for each slot inside.
   */
  slotProps?: PageHeaderSlotProps;
}

export function PageHeader(props: PageHeaderProps) {
  const { breadcrumbs, title, subtitle } = props;

  const matches = useMatches() as UIMatch<unknown, RouteHandle>[];

  // Get breadcrumb data from route handles
  const matchedBreadcrumbs = matches
    .filter((match) => match.handle?.breadcrumb)
    .map((match) => {
      if (typeof match.handle.breadcrumb === 'function') {
        return {
          title: match.handle.breadcrumb(match),
          path: match.pathname,
        };
      }
      return {
        title: match.handle.breadcrumb,
        path: match.pathname,
      };
    });

  const resolvedBreadcrumbs = breadcrumbs ?? matchedBreadcrumbs ?? [];
  const resolvedTitle = title ?? resolvedBreadcrumbs[resolvedBreadcrumbs.length - 1]?.title ?? null;

  const ToolbarComponent = props?.slots?.toolbar ?? PageHeaderToolbar;
  const toolbarSlotProps = useSlotProps({
    elementType: ToolbarComponent,
    ownerState: props,
    externalSlotProps: props?.slotProps?.toolbar,
    additionalProps: {},
  });

  return (
    <Stack spacing={1} flex={1}>
      {typeof resolvedTitle === 'string' && (
        <Helmet>
          <title>{resolvedTitle.trim()}</title>
        </Helmet>
      )}

      {resolvedBreadcrumbs?.length > 1 && (
        <Breadcrumbs aria-label="breadcrumb">
          {resolvedBreadcrumbs?.map((item, index) => {
            if (item.path) {
              return (
                <Link key={index} component={RouterLink} underline="hover" color="inherit" to={item.path}>
                  {item.title}
                </Link>
              );
            }

            return (
              <Typography key={index} color="text.primary">
                {/* {getItemTitle(item)} */}
              </Typography>
            );
          })}
        </Breadcrumbs>
      )}

      <PageContentHeader>
        <Stack justifyContent="center">
          {resolvedTitle && (
            <Typography variant="h4" color="textPrimary">
              {resolvedTitle}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="h5" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Stack>

        {/* If title and subtitle accept ReactNode */}
        {/* {typeof resolvedTitle === 'string' ? <Typography variant="h4">{resolvedTitle}</Typography> : resolvedTitle}
        {typeof subtitle === 'string' ? <Typography variant="h4">{subtitle}</Typography> : subtitle} */}

        <ToolbarComponent {...toolbarSlotProps} />
      </PageContentHeader>
    </Stack>
  );
}
